<template>
  <div class="rb-ob-form">
    <header>
      <h1 class="rb-ob-title">{{ titleText }}</h1>
    </header>

    <div class="rb-ob-status-panel" :class="{ 'is-urgent': isUrgent }">
      <p class="rb-ob-privacy-note">{{ introText }}</p>
      <p v-if="isRequirementReview && requirementReview.support_case_id" class="rb-ob-privacy-note">
        {{ $route.params.lang === 'en' ? 'Follow-up reference' : 'کد پیگیری بررسی' }}:
        <bdi>{{ requirementReview.support_case_id }}</bdi>
      </p>
      <p v-if="isRequirementReview && !requirementReview.followup_available" class="rb-ob-status" role="status">
        {{ $route.params.lang === 'en' ? 'A follow-up reference is not available. Ask support to check this intake.' : 'کد پیگیری در دسترس نیست. می‌توانی از پشتیبانی بخواهی وضعیت همین پذیرش را بررسی کند.' }}
      </p>
      <p v-if="loadError && showsCrisisContacts" class="rb-ob-err is-visible" role="alert">{{ loadError }}</p>
      <ul v-if="showsCrisisContacts && contacts.length" class="rb-ob-contact-list">
        <li v-for="item in contacts" :key="item.tel + item.id">
          <a :href="'tel:' + item.tel">
            <span class="rb-contact-name">{{ contactLabel(item) }}</span>
            <span class="rb-contact-action"><bdi dir="ltr">{{ contactNumber(item.tel) }}</bdi><span>{{ $t('intake.crisisContactCall') }}</span></span>
          </a>
        </li>
      </ul>
      <div
          v-else-if="showsCrisisContacts && showCrisisFallback"
          class="rb-ob-privacy-note"
          role="status"
      >
        <p>{{ $t('intake.crisisSeeHelp') }}</p>
        <p>{{ $t('intake.emergencyIfIran') }}</p>
      </div>
    </div>

    <section v-if="kind === 'specialist' && reviewLoaded" class="rb-ob-field-block" aria-live="polite">
      <h2>{{ $t('intake.disp.specialist.reviewTitle') }}</h2>
      <article v-for="row in specialistReview" :key="row.id" class="rb-ob-field-block">
        <strong>{{ row.label }}</strong>
        <p>{{ row.values.join(' · ') }}</p>
      </article>
      <p v-if="!canEditSpecialistReview" class="rb-ob-privacy-note">{{ $t('intake.disp.specialist.reviewUnavailable') }}</p>
    </section>

    <div class="rb-ob-actions">
      <button
          class="rb-ob-submit"
          type="button"
          :disabled="submitting"
          @click="onAction"
      >
        {{ actionText }}
      </button>
      <button
          v-if="showClarifyHelpAction"
          class="rb-ob-submit rb-ob-submit-secondary"
          type="button"
          :disabled="submitting"
          @click="onClarifyHelp"
      >
        {{ $t('intake.disp.more.clarifySafety.helpAction') }}
      </button>
      <button
          v-if="kind === 'specialist'"
          class="rb-ob-submit rb-ob-submit-secondary"
          type="button"
          :disabled="submitting"
          @click="onReviewSpecialist"
      >{{ $t('intake.disp.specialist.reviewAction') }}</button>
      <button
          v-if="kind === 'specialist' && reviewLoaded"
          class="rb-ob-submit rb-ob-submit-secondary"
          type="button"
          :disabled="submitting || !canEditSpecialistReview"
          @click="onEditSpecialist"
      >{{ $t('intake.disp.specialist.editAction') }}</button>
      <button
          v-if="kind === 'specialist'"
          class="rb-ob-submit rb-ob-submit-secondary"
          type="button"
          :disabled="submitting"
          @click="onRestartSpecialist"
      >{{ $t('intake.disp.specialist.restartAction') }}</button>
      <button
          v-if="kind === 'specialist'"
          class="rb-ob-submit rb-ob-submit-secondary"
          type="button"
          :disabled="submitting"
          @click="goCrisis"
      >
        {{ $t('intake.crisisSeeHelp') }}
      </button>
      <router-link
          v-if="kind !== 'ops'"
          class="rb-ob-submit rb-ob-submit-secondary"
          :to="supportLink"
      >{{ $t('intake.disp.accountSupportAction') }}</router-link>
      <p class="rb-ob-privacy-note">{{ $t('intake.disp.accountSupportNote') }}</p>
      <transition name="rb-ob-swap">
        <intake-emergency-box
            v-if="showHelp"
            :contacts="contacts"
            @open-crisis="goCrisis"
        />
      </transition>
      <p class="rb-ob-status" role="status" aria-live="polite">{{ formStatus }}</p>
      <div class="rb-ob-trust">
        <p>{{ $t('intake.trustPath') }}</p>
        <p>{{ $t('authUi.familyNote') }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import IntakeEmergencyBox from './IntakeEmergencyBox.vue'
import {clientError} from '@/intake/clientMessages'
import {toFaDigits} from '@/intake/intakeState'
import {EventBus} from '@/event-bus'
import {clearMatchSession} from '@/intake/sandboxMatchSession'
import {
  applySessionToStore,
  clearDispMoreVariant,
  clearFinalizedSnapshot,
  clearScreeningStore,
  clarificationRecoveryRoute,
  dispositionKindIsUrgent,
  getDispMoreVariant,
  isImmutableMoreAnswersIntake,
  loadScreeningStore,
  restartScreeningStore,
} from '@/intake/screeningSession'
import {getScreeningSession, prevScreening} from '@/intake/sandboxMatchApi'
import {
  crisisContactsUnavailable,
  dispositionShowsCrisisContacts,
  loadCrisisResourceResult,
} from '@/intake/crisisResources'

const KIND_SET = {
  more: true,
  unsupported: true,
  private: true,
  specialist: true,
  ops: true,
}

export default {
  name: 'IntakeDispStep',
  components: {IntakeEmergencyBox},
  data() {
    return {
      showHelp: false,
      formStatus: '',
      contacts: [],
      loadError: '',
      registryUnavailable: false,
      moreVariant: null,
      submitting: false,
      reviewLoaded: false,
      reviewSession: null,
      pageDisposed: false,
    }
  },
  computed: {
    isSpecialistActive() {
      return !this.pageDisposed && this.kind === 'specialist' && this.$route.name === 'intakeDisp'
    },
    specialistReview() {
      return (this.reviewSession && this.reviewSession.review) || []
    },
    canEditSpecialistReview() {
      return !!this.reviewSession && this.reviewSession.state !== 'FINALIZED' && !!this.reviewSession.can_go_back
    },
    supportLink() {
      return {
        name: 'portalRequestNew',
        params: {lang: this.$route.params.lang || 'fa'},
        query: {topic: 'ACCESS', from: 'intake-help', kind: this.kind},
      }
    },
    kind() {
      const raw = String(this.$route.params.kind || '').toLowerCase()
      return KIND_SET[raw] ? raw : 'more'
    },
    isUrgent() {
      return dispositionKindIsUrgent(this.kind)
    },
    showsCrisisContacts() {
      return dispositionShowsCrisisContacts(this.kind)
    },
    isClarifySafety() {
      return this.kind === 'more' && this.moreVariant === 'clarifySafety'
    },
    requirementReview() {
      const session = loadScreeningStore().session
      const review = session && session.completion_review
      return review && review.required === true && review.kind === 'REQUIREMENT_POLICY_REVIEW' ? review : null
    },
    isRequirementReview() {
      return this.kind === 'more' && !!this.requirementReview
    },
    isImmutableMore() {
      if (this.kind !== 'more') {
        return false
      }
      const store = loadScreeningStore()
      return isImmutableMoreAnswersIntake(store.session)
    },
    showClarifyHelpAction() {
      return this.isClarifySafety && !this.isImmutableMore
    },
    titleText() {
      if (this.isRequirementReview) return this.$route.params.lang === 'en'
        ? 'Your next step needs care-team review' : 'قدم بعدی تو به بررسی تیم مراقبت نیاز دارد'
      if (this.isImmutableMore) {
        return this.$t('intake.disp.more.terminalFinalized.title')
      }
      if (this.isClarifySafety) {
        return this.$t('intake.disp.more.clarifySafety.title')
      }
      return this.$t('intake.disp.' + this.kind + '.title')
    },
    introText() {
      if (this.isRequirementReview) return this.$route.params.lang === 'en'
        ? 'Your answers are saved. A history or care-coordination requirement needs review before routine therapist suggestions can continue. This is separate from an immediate-safety decision. Restarting the form does not resolve this requirement.'
        : 'پاسخ‌هایت ثبت شده‌اند. پیش از ادامهٔ پیشنهاد معمول درمانگر، بخشی از سابقه یا نیاز به هماهنگی مراقبت باید بررسی شود. این بررسی با تصمیم دربارهٔ خطر فوری فرق دارد؛ شروع دوبارهٔ فرم این نیاز را برطرف نمی‌کند.'
      if (this.kind === 'more') {
        if (this.isImmutableMore) {
          return this.$t('intake.disp.more.terminalFinalized.intro')
        }
        if (this.isClarifySafety) {
          return this.$t('intake.disp.more.clarifySafety.intro')
        }
        return this.$t('intake.disp.more.intro')
      }
      if (this.kind === 'unsupported') {
        return this.$t('intake.disp.unsupported.intro')
      }
      return this.$t('intake.disp.sharedIntro')
    },
    actionText() {
      if (this.isRequirementReview) return this.$route.params.lang === 'en'
        ? 'Back to my portal' : 'بازگشت به پرتال'
      if (this.isImmutableMore) {
        return this.$t('intake.disp.more.terminalFinalized.action')
      }
      if (this.isClarifySafety) {
        return this.$t('intake.disp.more.clarifySafety.action')
      }
      return this.$t('intake.disp.' + this.kind + '.action')
    },
    showCrisisFallback() {
      if (!this.showsCrisisContacts) {
        return false
      }
      return crisisContactsUnavailable(this.contacts, this.registryUnavailable)
    },
  },
  watch: {
    '$route.params.kind'() {
      if (this.$route.name !== 'intakeDisp') return
      this.showHelp = false
      this.formStatus = ''
      this.reviewLoaded = false
      this.reviewSession = null
      this.moreVariant = getDispMoreVariant()
      if (!KIND_SET[String(this.$route.params.kind || '').toLowerCase()]) {
        this.$router.replace({
          name: 'intakeDisp',
          params: {lang: this.$route.params.lang || 'fa', kind: 'more'},
        })
        return
      }
      this.fetchContacts()
    },
  },
  created() {
    this.moreVariant = getDispMoreVariant()
    if (!KIND_SET[String(this.$route.params.kind || '').toLowerCase()]) {
      this.$router.replace({
        name: 'intakeDisp',
        params: {lang: this.$route.params.lang || 'fa', kind: 'more'},
      })
      return
    }
    this.fetchContacts()
  },
  mounted() {
    EventBus.$on('intake-specialist-review', this.onEditSpecialist)
  },
  beforeDestroy() {
    this.pageDisposed = true
    EventBus.$off('intake-specialist-review', this.onEditSpecialist)
    this.reviewSession = null
  },
  methods: {
    contactNumber(tel) { return this.$route.params.lang === 'en' ? tel : toFaDigits(tel) },
    async loadSpecialistSession() {
      const store = loadScreeningStore()
      if (!store.intakeId) {
        this.formStatus = this.$t('intake.disp.specialist.reviewUnavailable')
        return null
      }
      const session = await getScreeningSession(store.intakeId)
      if (!this.isSpecialistActive) return null
      if (!session || String(session.intake_id) !== String(store.intakeId)) {
        this.formStatus = this.$t('intake.disp.specialist.reviewUnavailable')
        return null
      }
      // Refresh the write revision before /prev; review values remain in memory.
      applySessionToStore(session)
      this.reviewSession = session
      return session
    },
    async onReviewSpecialist() {
      if (!this.isSpecialistActive || this.submitting) return
      this.submitting = true
      this.formStatus = ''
      try {
        if (await this.loadSpecialistSession()) this.reviewLoaded = true
      } catch (error) {
        this.formStatus = clientError(error, this.$route.params.lang || 'fa')
      } finally {
        this.submitting = false
      }
    },
    async onEditSpecialist() {
      if (!this.isSpecialistActive || this.submitting) return
      this.submitting = true
      this.formStatus = ''
      try {
        const session = await this.loadSpecialistSession()
        if (!session || !this.canEditSpecialistReview) {
          this.formStatus = this.$t('intake.disp.specialist.reviewUnavailable')
          return
        }
        const reopened = await prevScreening(session.intake_id)
        if (!this.isSpecialistActive) return
        if (!reopened || String(reopened.intake_id) !== String(session.intake_id)) {
          this.formStatus = this.$t('intake.disp.specialist.reviewUnavailable')
          return
        }
        applySessionToStore(reopened)
        if (!reopened.next_question || reopened.state === 'FINALIZED') {
          this.formStatus = this.$t('intake.disp.specialist.reviewUnavailable')
          return
        }
        // Only a successful server undo reopens screening. Never clear a
        // terminal decision locally or restart automatically after a failure.
        clearFinalizedSnapshot()
        clearMatchSession()
        await this.$router.push({name: 'intakeScreening', params: {lang: this.$route.params.lang || 'fa'}})
      } catch (error) {
        this.formStatus = clientError(error, this.$route.params.lang || 'fa')
      } finally {
        this.submitting = false
      }
    },
    onRestartSpecialist() {
      if (!this.isSpecialistActive || this.submitting) return
      if (!window.confirm(this.$t('intake.screeningUi.restartConfirm'))) return
      restartScreeningStore()
      clearMatchSession()
      this.$router.push({
        name: 'intakeScreening',
        params: {lang: this.$route.params.lang || 'fa'},
        query: {restart: '1'},
      })
    },
    contactLabel(item) {
      const key = 'intake.crisisContact.' + item.id
      if (this.$te && this.$te(key)) {
        return this.$t(key)
      }
      return item.displayName || item.id
    },
    async fetchContacts() {
      this.contacts = []
      this.loadError = ''
      this.registryUnavailable = false
      if (!dispositionShowsCrisisContacts(this.kind)) {
        return
      }
      const result = await loadCrisisResourceResult('IR')
      this.contacts = result.contacts || []
      this.registryUnavailable = !!result.registryUnavailable
      if (this.registryUnavailable) {
        this.loadError = this.$t('intake.crisisSeeHelp')
      }
    },
    async onClarifyHelp() {
      this.showHelp = !this.showHelp
      if (this.showHelp && !this.contacts.length) {
        const result = await loadCrisisResourceResult('IR')
        this.contacts = result.contacts || []
        this.registryUnavailable = !!result.registryUnavailable
      }
    },
    goCrisis() {
      this.$router.push({
        name: 'intakeCrisis',
        params: {lang: this.$route.params.lang || 'fa'},
      })
    },
    async onSpecialistHelp() {
      this.showHelp = !this.showHelp
      if (this.showHelp && !this.contacts.length) {
        const result = await loadCrisisResourceResult('IR')
        this.contacts = result.contacts || []
        this.registryUnavailable = !!result.registryUnavailable
      }
    },
    async onAction() {
      const lang = this.$route.params.lang || 'fa'
      if (this.isRequirementReview) {
        this.$router.push({name: 'portalHome', params: {lang}})
        return
      }
      if (this.kind === 'more') {
        this.submitting = true
        this.formStatus = ''
        try {
          // Finalized MORE_ANSWERS is immutable: /prev returns 409 INTAKE_FINALIZED.
          // Offer restart (and optional support note) instead of a dead-end "answer more" loop.
          if (this.isImmutableMore) {
            clearScreeningStore()
            restartScreeningStore()
            this.formStatus = this.$t('intake.disp.more.terminalFinalized.supportHint')
            this.$router.push({
              name: 'intakeScreening',
              params: {lang},
              query: {restart: '1'},
            })
            return
          }

          // Undo the terminal answer (usually R8) so screening reopens that question.
          clearFinalizedSnapshot()
          const store = loadScreeningStore()
          if (store.intakeId) {
            try {
              const session = await prevScreening(store.intakeId)
              if (session && session.intake_id) {
                applySessionToStore(session)
                clearDispMoreVariant()
                this.$router.push({
                  name: 'intakeScreening',
                  params: {lang},
                })
                return
              }
            } catch (e) {
              const code = e && e.code ? String(e.code) : ''
              if (code === 'INTAKE_FINALIZED' || (e && e.status === 409)) {
                clearScreeningStore()
                restartScreeningStore()
                this.formStatus = this.$t('intake.disp.more.terminalFinalized.supportHint')
                this.$router.push({
                  name: 'intakeScreening',
                  params: {lang},
                  query: {restart: '1'},
                })
                return
              }
              this.formStatus = clientError(e, lang)
              // Fall through to resume/restart route below.
            }
          }
          const route = clarificationRecoveryRoute(lang)
          if (route.query && route.query.restart) {
            restartScreeningStore()
          }
          this.$router.push(route)
        } finally {
          this.submitting = false
        }
        return
      }
      if (this.kind === 'unsupported') {
        restartScreeningStore()
        this.$router.push({
          name: 'intakeScreening',
          params: {lang},
          query: {restart: '1'},
        })
        return
      }
      if (this.kind === 'private') {
        // Terminal private follow-up must not resume the same intake (redirect loop).
        clearScreeningStore()
        restartScreeningStore()
        this.$router.push({
          name: 'intakeScreening',
          params: {lang},
          query: {restart: '1'},
        })
        return
      }
      if (this.kind === 'specialist') {
        await this.onSpecialistHelp()
        return
      }
      // Opening the support form is not a submitted request. Only its server receipt
      // can confirm delivery; keep the terminal screening disposition unchanged.
      this.$router.push(this.supportLink)
    },
  },
}
</script>
